import { Component, OnDestroy, OnInit, computed, effect, signal } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { UserSessionService } from './core/user-session.service';
import { environment } from '../environments/environment';
import { AuthStateService } from './core/auth-state.service';
import { NotificationService } from './features/notifications/services/notification.service';
import { Subscription, interval, startWith, switchMap } from 'rxjs';
import { SidebarPanelComponent } from './shared/components/sidebar-panel/sidebar-panel.component';
import { SidebarService } from './shared/services/sidebar.service';
import { SidebarPanelType } from './shared/models/sidebar-panel';
import { LoadingOverlayComponent } from './shared/components/loading-overlay/loading-overlay.component';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, RouterLink, RouterLinkActive, SidebarPanelComponent, LoadingOverlayComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit, OnDestroy {
  private readonly sessionCheckIntervalMs = 1000 * 300;
  private sessionCheckSubscription?: Subscription;
  private readonly authLoadStarted = signal(false);
  private readonly authBootstrapped = signal(false);

  protected readonly title = signal('PalTrax.CustomerPortalV2');
  protected readonly apiUrl = signal(environment.apiUrl);
  protected readonly sessionCheckStatus = signal('not started');
  protected readonly showBootLoader = computed(
    () => this.authLoadStarted() && !this.authBootstrapped() && this.authStateService.loading()
  );

  constructor(
    private readonly userSessionService: UserSessionService,
    private readonly authStateService: AuthStateService,
    private readonly notificationService: NotificationService,
    private readonly sidebarService: SidebarService
  ) {
    effect(() => {
      if (this.authStateService.loading()) {
        this.authLoadStarted.set(true);
      }

      if (this.authLoadStarted() && !this.authStateService.loading()) {
        this.authBootstrapped.set(true);
      }
    });
  }

  ngOnInit(): void {
    this.authStateService.initialize();
    this.notificationService.refresh();
    this.sessionCheckSubscription = interval(this.sessionCheckIntervalMs)
      .pipe(startWith(0), switchMap(() => this.userSessionService.checkExpiration()))
      .subscribe({
        next: (isExpired) => {
          this.sessionCheckStatus.set(isExpired ? 'expired' : 'active');
        },
        error: () => {
          this.sessionCheckStatus.set('failed');
        }
      });
  }

  ngOnDestroy(): void {
    this.sessionCheckSubscription?.unsubscribe();
  }

  canAccessSalesOrders(): boolean {
    return this.authStateService.canAccessSalesOrders();
  }

  canAccessCprs(): boolean {
    return this.authStateService.canAccessCprs();
  }

  canAccessManageOrders(): boolean {
    return this.canAccessSalesOrders() || this.canAccessCprs();
  }

  canAccessReports(): boolean {
    return this.authStateService.canAccessReports();
  }

  canAccessAdministration(): boolean {
    return this.authStateService.canAccessAdministration();
  }

  logout(): void {
    this.authStateService.logout();
  }

  payInvoice(): void {
    const proceed = window.confirm(
      'NOTICE: You have clicked on a link which leaves 48forty Solutions website, and you will be redirected to a third party website. 48forty Solutions makes no representations nor has any supervision or control over the quality, content, reliability or security of the third party website, nor shall be liable for its use.'
    );

    if (proceed) {
      window.open('https://new.mytitaniumflex.com/selfPayment/650c44f2970a6b1687e484ec', '_blank');
    }
  }

  openNotifications(): void {
    this.sidebarService.open({ type: SidebarPanelType.NotificationList });
  }

  openSearch(): void {
    this.sidebarService.open({ type: SidebarPanelType.SearchResults });
  }

  unreadNotificationCount(): number {
    return this.notificationService.unreadCount();
  }
}
